import React, { useEffect, useState } from "react";
import ToolShell from "@/components/ToolShell";
import CurrencyInput from "@/components/CurrencyInput";
import { calculateSAI, estimatePell, compareScenarios } from "@/lib/sai";
import { track } from "@/lib/analytics";
import { TrendingDown, ShieldCheck } from "lucide-react";

export default function EstimatorPage() {
  const [inputs, setInputs] = useState({ parentAGI: "", studentAGI: "", parentAssets: "", numberInCollege: 1 });
  const [result, setResult] = useState(null);

  useEffect(() => { track.tool_opened("estimator"); }, []);

  const set = (k, v) => setInputs(p => ({ ...p, [k]: v }));

  const calc = (e) => {
    e?.preventDefault();
    const numericInputs = {
      parentAGI: Number(inputs.parentAGI) || 0,
      studentAGI: Number(inputs.studentAGI) || 0,
      parentAssets: Number(inputs.parentAssets) || 0,
      numberInCollege: inputs.numberInCollege,
    };
    const sai = calculateSAI(numericInputs);
    const pell = estimatePell(sai);
    const scenarios = compareScenarios(numericInputs);
    setResult({ sai, pell, scenarios });
    track.estimator_calculated();
  };

  return (
    <ToolShell tool="Tool 03 · SAI Estimator" title="Your Student Aid Index, estimated." subtitle="Enter your family's financials. The calculation runs entirely in your browser, these numbers never leave this page.">
      <form onSubmit={calc} className="card grid sm:grid-cols-2 gap-5" data-testid="estimator-form">
        <CurrencyInput id="est-parent-agi"    label="Parent adjusted gross income (AGI)"    value={inputs.parentAGI}     onChange={(v) => set("parentAGI", v)}    placeholder="85,000" testid="est-parent-agi" />
        <CurrencyInput id="est-student-agi"   label="Student adjusted gross income (AGI)"   value={inputs.studentAGI}    onChange={(v) => set("studentAGI", v)}   placeholder="0"      testid="est-student-agi" />
        <CurrencyInput id="est-parent-assets" label="Parent assets (savings + investments, not retirement)" value={inputs.parentAssets} onChange={(v) => set("parentAssets", v)} placeholder="45,000" testid="est-parent-assets" />
        <div>
          <label className="block text-sm text-ink-muted mb-2">Number of family members in college</label>
          <input type="number" min="1" className="input" value={inputs.numberInCollege} onChange={e => set("numberInCollege", Number(e.target.value) || 1)} data-testid="est-num-college" />
        </div>
        <div className="sm:col-span-2">
          <button type="submit" className="btn btn-primary" data-testid="est-calculate">Calculate my SAI</button>
        </div>
      </form>

      {result && (
        <section className="mt-8" data-testid="estimator-result">
          <div className="grid sm:grid-cols-2 gap-5 mb-8">
            <div className="card">
              <div className="text-xs uppercase tracking-widest text-ink-dim mb-1">Your estimated SAI</div>
              <div className="font-heading text-5xl text-ink" data-testid="est-sai-value">${result.sai.toLocaleString()}</div>
            </div>
            <div className="card">
              <div className="text-xs uppercase tracking-widest text-ink-dim mb-1">Estimated Pell Grant (federal)</div>
              <div className="font-heading text-5xl text-success">${result.pell.toLocaleString()}</div>
              <div className="text-xs text-ink-dim mt-2">2025-26 maximum is $7,395.</div>
            </div>
          </div>

          <h2 className="font-heading text-3xl mb-4">What-if scenarios</h2>
          <div className="space-y-4">
            {result.scenarios.length === 0 && (
              <div className="card text-ink-muted text-sm">No additional optimization moves for these inputs.</div>
            )}
            {result.scenarios.map(s => (
              <div key={s.id} className="card" data-testid={`est-scenario-${s.id}`}>
                <div className="flex items-start gap-3">
                  <TrendingDown className="w-5 h-5 text-accent mt-1" />
                  <div className="flex-1">
                    <h3 className="font-heading text-2xl text-ink">{s.label}</h3>
                    <p className="text-sm text-ink-muted mt-1">{s.description}</p>
                    <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
                      <div><div className="text-ink-dim text-xs">Current SAI</div><div className="text-ink font-heading text-xl">${s.before.toLocaleString()}</div></div>
                      <div><div className="text-ink-dim text-xs">New SAI</div><div className="text-ink font-heading text-xl">${s.after.toLocaleString()}</div></div>
                      <div><div className="text-ink-dim text-xs">Change</div>
                        {s.before - s.after > 0 ? (
                          <div className="text-success font-heading text-xl">−${(s.before - s.after).toLocaleString()}</div>
                        ) : (
                          <div className="text-ink-dim font-heading text-xl">no change</div>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 text-sm text-ink bg-surface rounded-lg p-3"><strong>Action:</strong> {s.action}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 card border-border">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-success mt-0.5" />
              <p className="text-xs text-ink-muted">Informational only, not financial advice. Consult a financial aid professional before making changes to your finances. Nothing you entered here was sent or saved.</p>
            </div>
          </div>
        </section>
      )}
    </ToolShell>
  );
}
